import {
  type GoalTreeEnvelope,
  GoalTreeEnvelopeSchema,
} from '@streamlain/view-config';

export interface GoalPlanHandle {
  readonly goalId: string;
  readonly status?: string | null;
  readonly objective?: string | null;
  readonly blocker?: GoalPlanBlocker | null;
  readonly goalTree: GoalTreeEnvelope;
}

export interface GoalListSummary {
  readonly goalId: string;
  readonly status?: string | null;
  readonly objective?: string | null;
  readonly goalTree?: GoalTreeEnvelope | null;
}

export interface GoalPlanBlocker {
  readonly code: string;
  readonly kind?: string | null;
  readonly message: string;
  readonly options: readonly GoalPlanBlockerOption[];
}

export interface GoalPlanBlockerOption {
  readonly id: string;
  readonly label: string;
}

export interface GoalReference {
  readonly goalId: string;
  readonly status?: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function parseBlocker(value: unknown): GoalPlanBlocker | null {
  if (!isRecord(value)) return null;
  const code = readString(value.code);
  const message = readString(value.message);
  if (!code || !message) return null;
  const options = Array.isArray(value.options)
    ? value.options.flatMap(option => {
        if (!isRecord(option)) return [];
        const id = readString(option.id);
        if (!id) return [];
        return [{ id, label: readString(option.label) ?? id }];
      })
    : [];
  return {
    code,
    kind: readString(value.kind),
    message,
    options,
  };
}

export function parseGoalTreeCandidate(
  value: unknown
): GoalTreeEnvelope | null {
  const direct = GoalTreeEnvelopeSchema.safeParse(value);
  if (direct.success) return direct.data;
  if (!isRecord(value)) return null;
  const nestedCandidates = [
    value.goalTree,
    isRecord(value.data) ? value.data.goalTree : undefined,
    isRecord(value.result) ? value.result.goalTree : undefined,
    isRecord(value.result) && isRecord(value.result.data)
      ? value.result.data.goalTree
      : undefined,
    isRecord(value.goal) && isRecord(value.goal.currentState)
      ? value.goal.currentState.goalTree
      : undefined,
    isRecord(value.goal) && isRecord(value.goal.plan)
      ? value.goal.plan.goalTree
      : undefined,
  ];
  for (const candidate of nestedCandidates) {
    const parsed = GoalTreeEnvelopeSchema.safeParse(candidate);
    if (parsed.success) return parsed.data;
  }
  return null;
}

export function parseGoalPlanHandleCandidate(
  value: unknown
): GoalPlanHandle | null {
  if (!isRecord(value)) return null;
  const goalTree = parseGoalTreeCandidate(value);
  if (!goalTree) return null;
  const data = isRecord(value.data) ? value.data : null;
  const result = isRecord(value.result) ? value.result : null;
  const resultData = result && isRecord(result.data) ? result.data : null;
  const goal = isRecord(value.goal) ? value.goal : null;
  const nestedGoal = data && isRecord(data.goal) ? data.goal : null;
  const goalId =
    readString(value.goalId) ??
    readString(data?.goalId) ??
    readString(result?.goalId) ??
    readString(resultData?.goalId) ??
    readString(goal?.id) ??
    readString(nestedGoal?.id);
  if (!goalId) return null;
  return {
    goalId,
    status:
      readString(value.status) ??
      readString(data?.status) ??
      readString(result?.status) ??
      readString(resultData?.status) ??
      readString(goal?.status) ??
      readString(nestedGoal?.status),
    objective:
      readString(value.objective) ??
      readString(data?.objective) ??
      readString(result?.objective) ??
      readString(resultData?.objective) ??
      readString(goal?.objective) ??
      readString(nestedGoal?.objective),
    blocker:
      parseBlocker(value.blocker) ??
      parseBlocker(data?.blocker) ??
      parseBlocker(result?.blocker) ??
      parseBlocker(resultData?.blocker) ??
      parseBlocker(goal?.blocker) ??
      parseBlocker(nestedGoal?.blocker),
    goalTree,
  };
}

export function parseGoalListSummaryCandidate(
  value: unknown
): GoalListSummary | null {
  const wrapped =
    isRecord(value) && isRecord(value.goal) ? value : { goal: value };
  const goal = isRecord(wrapped.goal) ? wrapped.goal : null;
  const goalId = readString(goal?.id);
  if (!goalId) return null;
  const plan = parseGoalPlanHandleCandidate(wrapped);
  return {
    goalId,
    status: readString(goal?.status),
    objective: readString(goal?.objective),
    goalTree: plan?.goalTree ?? null,
  };
}

export function parseGoalReferenceCandidate(
  value: unknown
): GoalReference | null {
  if (!isRecord(value)) return null;
  const data = isRecord(value.data) ? value.data : null;
  const result = isRecord(value.result) ? value.result : null;
  const resultData = result && isRecord(result.data) ? result.data : null;
  const goal = isRecord(value.goal) ? value.goal : null;
  const nestedGoal = data && isRecord(data.goal) ? data.goal : null;
  const goalId =
    readString(value.goalId) ??
    readString(data?.goalId) ??
    readString(result?.goalId) ??
    readString(resultData?.goalId) ??
    readString(goal?.id) ??
    readString(nestedGoal?.id);
  if (!goalId) return null;
  return {
    goalId,
    status:
      readString(value.status) ??
      readString(data?.status) ??
      readString(result?.status) ??
      readString(resultData?.status) ??
      readString(goal?.status) ??
      readString(nestedGoal?.status),
  };
}

export function extractLatestGoalTreeFromMessages(
  messages: readonly unknown[]
): GoalTreeEnvelope | null {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = messages[messageIndex];
    if (!isRecord(message)) continue;
    const streamObjects = Array.isArray(message.streamObjects)
      ? message.streamObjects
      : [];
    for (
      let objectIndex = streamObjects.length - 1;
      objectIndex >= 0;
      objectIndex--
    ) {
      const object = streamObjects[objectIndex];
      if (!isRecord(object) || object.type !== 'tool-result') continue;
      const parsed = parseGoalTreeCandidate(object.result);
      if (parsed) return parsed;
    }
    const toolData = isRecord(message.toolData) ? message.toolData : null;
    const toolResults = Array.isArray(toolData?.toolResults)
      ? toolData.toolResults
      : [];
    for (
      let resultIndex = toolResults.length - 1;
      resultIndex >= 0;
      resultIndex--
    ) {
      const parsed = parseGoalTreeCandidate(toolResults[resultIndex]);
      if (parsed) return parsed;
    }
  }
  return null;
}

export function extractLatestGoalPlanFromMessages(
  messages: readonly unknown[]
): GoalPlanHandle | null {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = messages[messageIndex];
    if (!isRecord(message)) continue;
    const streamObjects = Array.isArray(message.streamObjects)
      ? message.streamObjects
      : [];
    for (
      let objectIndex = streamObjects.length - 1;
      objectIndex >= 0;
      objectIndex--
    ) {
      const object = streamObjects[objectIndex];
      if (!isRecord(object) || object.type !== 'tool-result') continue;
      const parsed = parseGoalPlanHandleCandidate(object.result);
      if (parsed) return parsed;
    }
    const toolData = isRecord(message.toolData) ? message.toolData : null;
    const toolResults = Array.isArray(toolData?.toolResults)
      ? toolData.toolResults
      : [];
    for (
      let resultIndex = toolResults.length - 1;
      resultIndex >= 0;
      resultIndex--
    ) {
      const parsed = parseGoalPlanHandleCandidate(toolResults[resultIndex]);
      if (parsed) return parsed;
    }
  }
  return null;
}

export function extractLatestGoalReferenceFromMessages(
  messages: readonly unknown[]
): GoalReference | null {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex--
  ) {
    const message = messages[messageIndex];
    if (!isRecord(message)) continue;
    const streamObjects = Array.isArray(message.streamObjects)
      ? message.streamObjects
      : [];
    for (
      let objectIndex = streamObjects.length - 1;
      objectIndex >= 0;
      objectIndex--
    ) {
      const object = streamObjects[objectIndex];
      if (!isRecord(object) || object.type !== 'tool-result') continue;
      const parsed = parseGoalReferenceCandidate(object.result);
      if (parsed) return parsed;
    }
    const toolData = isRecord(message.toolData) ? message.toolData : null;
    const toolCall = isRecord(toolData?.toolCall) ? toolData.toolCall : null;
    const toolArguments = isRecord(toolCall?.arguments)
      ? toolCall.arguments
      : null;
    if (toolCall?.name === 'agent_goal') {
      const parsed = parseGoalReferenceCandidate(toolArguments);
      if (parsed) return parsed;
    }
    const toolResults = Array.isArray(toolData?.toolResults)
      ? toolData.toolResults
      : [];
    for (
      let resultIndex = toolResults.length - 1;
      resultIndex >= 0;
      resultIndex--
    ) {
      const parsed = parseGoalReferenceCandidate(toolResults[resultIndex]);
      if (parsed) return parsed;
    }
  }
  return null;
}
